/**
 * ProviderStatusBadge Component
 *
 * Displays a colored badge indicating provider status.
 */

import type { ReactElement } from "react";

/** Provider status type */
export type ProviderStatus = "active" | "inactive" | "archived" | "deleted";

/** Badge props */
export interface ProviderStatusBadgeProps {
  /** The provider status */
  readonly status: ProviderStatus;
  /** Additional CSS classes */
  readonly className?: string;
}

/** Color mapping for each status */
const STATUS_CONFIG: Record<ProviderStatus, { label: string; bg: string; text: string }> = {
  active: {
    label: "Active",
    bg: "bg-success/10",
    text: "text-success",
  },
  inactive: {
    label: "Inactive",
    bg: "bg-warning/10",
    text: "text-warning",
  },
  archived: {
    label: "Archived",
    bg: "bg-foreground-muted/10",
    text: "text-foreground-muted",
  },
  deleted: {
    label: "Deleted",
    bg: "bg-error/10",
    text: "text-error",
  },
};

/**
 * Renders a colored status badge for a provider.
 */
export function ProviderStatusBadge({
  status,
  className = "",
}: ProviderStatusBadgeProps): ReactElement {
  const config = STATUS_CONFIG[status];

  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${config.bg} ${config.text} ${className}`}
      data-testid="provider-status-badge"
    >
      {config.label}
    </span>
  );
}
